"use client";

import { Separator } from "@/components/ui/separator";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Story() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: bodyRef,
    isVisible: bodyVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: quoteRef,
    isVisible: quoteVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="978" id="company-story" className="relative overflow-hidden bg-[var(--color-background)] text-[var(--color-foreground)] py-24 md:py-32 lg:py-40">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute -bottom-32 -left-32 h-96 w-96 rounded-lg opacity-40 blur-3xl" style={{
      background: "var(--color-primary-300)"
    }} />

      <div className="relative mx-auto max-w-[1280px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          {}
          <div className="lg:col-span-8 xl:col-span-8 max-w-[920px]">
            {}
            <div ref={eyebrowRef} className={`mb-10 md:mb-14 transition-all duration-1000 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-default italic text-xs md:text-sm tracking-[0.25em] uppercase text-[var(--color-primary-500)]" style={{
              fontVariant: "small-caps"
            }}>
                20+ Years of Experience &middot; Dubai & DAFZA Presence &middot; Global B2B Network
              </p>
            </div>

            {}
            <h1 ref={headlineRef} className={`font-default font-light text-[var(--color-foreground)] mb-12 md:mb-16 transition-all duration-1000 ease-out ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
            fontSize: "clamp(2.75rem, 7vw, 5rem)",
            lineHeight: "0.95",
            letterSpacing: "-0.02em"
          }}>
              Reliability at scale.{" "}
              <span className="italic font-normal text-[var(--color-primary-500)]">Market leadership redefined.</span>
            </h1>

            {}
            <div ref={bodyRef} className={`max-w-[720px] transition-all duration-1000 delay-200 ease-out ${bodyVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-default text-[var(--color-foreground)]/85 mb-12" style={{
              fontSize: "1.125rem",
              lineHeight: "1.75"
            }}>
                <span className="font-default text-[var(--color-primary-500)] float-left mr-3 mt-2 leading-none" style={{
                fontSize: "5.5rem",
                lineHeight: "0.85"
              }}>
                  W
                </span>
                ith over two decades of specialized expertise in the global electronics trading sector, KTEX has established itself as a cornerstone of reliability. Operating from the prestigious Dubai Airport Freezone (DAFZA), we leverage one of the world&apos;s most strategic logistics hubs to ensure seamless international trade operations and unparalleled regulatory compliance for our partners.
              </p>

              <p className="font-default text-[var(--color-foreground)]/85 mb-12" style={{
              fontSize: "1.125rem",
              lineHeight: "1.75"
            }}>
                Our strength lies in our expansive worldwide B2B network, connecting top-tier international suppliers with high-growth markets. By maintaining rigorous standards of quality and transparency, we have built a reputation as the preferred partner for enterprises seeking scalable procurement solutions and consistent supply chain excellence across the electronics landscape.
              </p>

              <p className="font-default text-[var(--color-foreground)]/85 mb-12" style={{
              fontSize: "1.125rem",
              lineHeight: "1.75"
            }}>
                For our investors and stakeholders, KTEX represents a commitment to long-term value creation. Our business model is engineered for scalability, combining deep market intelligence with a robust financial foundation. We don&apos;t just participate in the market; we lead it by anticipating shifts in global demand and securing the strategic partnerships necessary for sustained growth.
              </p>
            </div>

            {}
            <div ref={quoteRef} className={`max-w-[760px] mx-auto my-16 md:my-20 text-center transition-all duration-1000 ease-out ${quoteVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <div aria-hidden="true" className="font-default text-[var(--color-accent-500)] leading-none mb-2" style={{
              fontSize: "3.5rem"
            }}>
                &ldquo;
              </div>
              <blockquote className="font-default italic text-[var(--color-primary-700)]" style={{
              fontSize: "clamp(1.5rem, 3vw, 2.25rem)",
              lineHeight: "1.3",
              letterSpacing: "-0.01em"
            }}>
                Our strategic vision is anchored in the belief that sustained growth is the natural byproduct of integrity, innovation, and an unwavering focus on B2B excellence.
              </blockquote>
              <div aria-hidden="true" className="font-default text-[var(--color-accent-500)] leading-none mt-2 rotate-180" style={{
              fontSize: "3.5rem"
            }}>
                &ldquo;
              </div>
            </div>

            {}
            <div className="max-w-[720px]">
              <p className="font-default text-[var(--color-foreground)]/85 mb-12" style={{
              fontSize: "1.125rem",
              lineHeight: "1.75"
            }}>
                Our methodology is built upon two inseparable pillars: innovation and reliability. Every project begins with a comprehensive analysis, not a pre-packaged solution. We listen to your unique needs, identify the core drivers of your business, and craft a roadmap that aligns perfectly with your long-term objectives.
              </p>

              <p className="font-default text-[var(--color-foreground)]/85 mb-12" style={{
              fontSize: "1.125rem",
              lineHeight: "1.75"
            }}>
                This is the philosophy of our firm. Not just service, but partnership. We are dedicated to showing up, fully prepared, to help you navigate the complexities of your industry. It is disciplined work. It is impactful work. And for those who choose to work with us, it is the beginning of a lasting, successful journey.
              </p>
            </div>

            {}
            <div className="flex items-center justify-center gap-4 max-w-[720px] my-12">
              <Separator className="flex-1 bg-[var(--color-primary-200)]" />
              <div aria-hidden="true" className="h-2 w-2 rounded-lg bg-[var(--color-accent-500)]" />
              <Separator className="flex-1 bg-[var(--color-primary-200)]" />
            </div>
          </div>

          {}
          <div className="hidden lg:block lg:col-span-4">
            <div ref={imageRef} className={`sticky top-24 transition-all duration-1000 delay-300 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
            marginTop: "33%"
          }}>
              <div className="relative aspect-[4/5] overflow-hidden shadow-lg" style={{
              borderRadius: "0.5rem"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/corporate_strategy_meeting_modern_office.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/corporate_strategy_meeting_modern_office.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/corporate_strategy_meeting_modern_office.webp-wvc-sizes" alt="A professional team collaborating in a modern, sunlit office space" loading="lazy" decoding="async" className="h-full w-full object-cover" />
                {}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay" style={{
                backgroundImage: "radial-gradient(var(--color-primary-900) 1px, transparent 1px)",
                backgroundSize: "3px 3px"
              }} />
              </div>

              {}
              <p className="mt-6 font-default italic text-sm text-[var(--color-primary-600)] max-w-[240px]" style={{
              lineHeight: "1.6"
            }}>
                Precision in every detail. Clarity in every decision.
              </p>
            </div>
          </div>

          {}
          <div className="lg:hidden mt-4">
            <div className="relative aspect-[4/5] overflow-hidden max-w-md mx-auto shadow-lg" style={{
            borderRadius: "0.5rem"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/corporate_strategy_meeting_modern_office.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/corporate_strategy_meeting_modern_office.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1680628/img/corporate_strategy_meeting_modern_office.webp-wvc-sizes" alt="A professional team collaborating in a modern, sunlit office space" loading="lazy" decoding="async" className="h-full w-full object-cover" />
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20 mix-blend-overlay" style={{
              backgroundImage: "radial-gradient(var(--color-primary-900) 1px, transparent 1px)",
              backgroundSize: "3px 3px"
            }} />
            </div>
            <p className="mt-6 font-default italic text-sm text-[var(--color-primary-600)] text-center max-w-[280px] mx-auto" style={{
            lineHeight: "1.6"
          }}>
              Precision in every detail. Clarity in every decision.
            </p>
          </div>
        </div>
      </div>
    </section>;
}